import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { IFormControlConfig } from '../../reactive-forms-ui/interfaces/form-control-config';
import { CustomDropdownControlConfig } from '../../reactive-forms-ui/models/custom-dropdown-control-config';
import { MatDialog } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { GlobalConfigService } from 'src/app/core/services/global-config.service';
import { PermissionsService } from 'src/app/core/services/permissions.service';
import { UserDataService } from 'src/app/core/services/user-data.service';
import { ManagedServicesRepository } from '../services/managed-services-repository.service';
import { ITicketCreationData } from '../../reactive-forms-ui/shared-components/ticket-creation-forms/models/tickets-creation-forms-config';
import { TicketCreationFormsComponent } from '../../reactive-forms-ui/shared-components/ticket-creation-forms/ticket-creation-forms.component';
import { ThreadMessengerService } from '../../secured-skeleton/services/thread-messenger.service';
import { FeaturesFlagsV2Service } from 'src/app/core/services/features-flags-v2.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackbarComponent } from 'src/app/core/components/snackbar/snackbar.component';
import { IMspDashboard } from '../models/msp-dashboard';
import { ManagedServicesSharedDataService } from '../services/managed-services-shared-data.service';

@Component({
  selector: 'managed-services',
  templateUrl: './managed-services.component.html',
  styleUrls: ['./managed-services.component.scss'],
  standalone: false,
})
export class ManagedServicesComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('titleContainer') titleContainer: ElementRef<HTMLElement>;
  @ViewChild('tabsContainer') tabsContainer: ElementRef<HTMLElement>;

  canAddTicket: boolean = false;
  dashboards: IMspDashboard[] = [];
  subtitle: string = '';
  tabsAreClose = false;

  dashboardsForm = new UntypedFormGroup({ dashboard: new UntypedFormControl([]) });
  formControlsConfigMap = new Map<string, IFormControlConfig>();

  private resizeObserver: ResizeObserver;
  private routeSubscription: Subscription;
  private dashboardsSubscription: Subscription;
  private formSubscription: Subscription;

  /**
   * True if the tickets header should be hidden
   */
  get hideTicketsHeader(): boolean {
    const [baseUrl] = this.router.url.split('?');
    const urlSegments = baseUrl.split('/');
    const ticketNumber = Number(urlSegments[4] ?? 'NaN');
    return urlSegments[2] === 'managed-services' && urlSegments[3] === 'tickets' && !isNaN(ticketNumber);
  }

  get isOnTicketsRoute(): boolean {
    const [baseUrl] = this.router.url.split('?');
    return baseUrl.split('/')[3] === 'tickets';
  }

  get showDashboardTabs(): boolean {
    const [baseUrl] = this.router.url.split('?');
    return baseUrl.split('/')[3] === 'dashboards';
  }

  get selectedDashboardTabIndex(): number {
    const [baseUrl] = this.router.url.split('?');
    const segments = baseUrl.split('/');
    return Math.max(
      0,
      this.dashboards.findIndex((d) => d.value === segments[4]),
    );
  }

  constructor(
    private router: Router,
    private activeRoute: ActivatedRoute,
    private dialog: MatDialog,
    private globalConfigService: GlobalConfigService,
    private userService: UserDataService,
    private permissionsService: PermissionsService,
    private managedServicesRepository: ManagedServicesRepository,
    private threadMessengerService: ThreadMessengerService,
    private featuresFlagsV2Service: FeaturesFlagsV2Service,
    private snackBar: MatSnackBar,
    private mspSharedDataService: ManagedServicesSharedDataService,
  ) {
    this.reviewAvailablePermissions();
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(() => this.checkProximity());
    this.resizeObserver.observe(this.titleContainer.nativeElement.closest('.managed-services-header'));
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.formSubscription?.unsubscribe();
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
    if (this.dashboardsSubscription) {
      this.dashboardsSubscription.unsubscribe();
    }
    this.threadMessengerService.hideMessenger();
  }

  private checkProximity(): void {
    const titleRect = this.titleContainer.nativeElement.getBoundingClientRect();
    const tabsRect = this.tabsContainer.nativeElement.getBoundingClientRect();
    this.tabsAreClose = tabsRect.left - titleRect.right < 5;
  }

  private syncDashboardFormValue(): void {
    const current = this.dashboards[this.selectedDashboardTabIndex]?.value;
    if (current) {
      this.dashboardsForm.get('dashboard').setValue([current], { emitEvent: false });
    }
  }

  onDashboardTabChange(event: MatTabChangeEvent): void {
    const dashboard = this.dashboards[event.index];
    if (dashboard) {
      this.router.navigate(['/secured', 'managed-services', 'dashboards', dashboard.value]);
    }
  }

  ngOnInit(): void {
    this.dashboardsSubscription = this.mspSharedDataService.dashboards$.subscribe((dashboards) => {
      this.dashboards = dashboards;
      this.formControlsConfigMap.set(
        'dashboard',
        new CustomDropdownControlConfig({
          options: dashboards.map((d) => ({ name: d.name, value: d.value })),
          placeholder: 'Select Dashboard',
        }),
      );
      this.syncDashboardFormValue();
    });

    this.formSubscription = this.dashboardsForm.get('dashboard').valueChanges.subscribe((values: string[]) => {
      if (values?.length) {
        this.router.navigate(['/secured', 'managed-services', 'dashboards', values[0]]);
      }
    });
    this.globalConfigService.fetchAllowedDomainsConfig();
    this.globalConfigService.fetchAllowedFiles();

    // After the user data is loaded, initialize the thread messenger
    // Only if the connection key is available
    this.featuresFlagsV2Service.featuresFlags$.subscribe((flags) => {
      const chatGenieFlag = flags.find((flag) => flag.feature === 'msp-chat-gennie');
      const isChatGenieEnabled = chatGenieFlag?.isActive ?? false;

      if (this.userService.user.messengerConnectionKey && isChatGenieEnabled) {
        this.threadMessengerService.setupMessenger(
          this.userService.user.email,
          this.userService.user.messengerConnectionKey,
        );
      }
    });

    const childData = this.activeRoute.firstChild?.snapshot.data;
    this.subtitle = childData?.['submodule'] ? `- ${childData['submodule'].toUpperCase()}` : '';

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const childData = this.activeRoute.firstChild?.snapshot.data;
        this.subtitle = childData?.['submodule'] ? `- ${childData['submodule'].toUpperCase()}` : '';
        this.syncDashboardFormValue();
      }
    });
  }

  /**
   * Overlay file component upload method
   * @param file File to upload
   * @returns Observable with the upload response
   */
  overlayFileComponentUpload(file: File, attachmentsPath: string): Observable<any> {
    return this.managedServicesRepository.uploadTmpFile(file, {
      path: attachmentsPath,
      generateThumbnail: true,
    });
  }

  /**
   * Overlay file component delete method
   * @param path Path of the file to delete
   * @returns Observable with the delete response
   */
  overlayFileComponentDelete(path: string, attachmentsPath: string): Observable<any> {
    return this.managedServicesRepository.deleteTmpFile({
      path: attachmentsPath + '/' + path,
      deleteThumbnail: true,
    });
  }

  onCreateTicket(ticketCreationData: ITicketCreationData, attachmentsPath: string): Observable<any> {
    return this.managedServicesRepository.postTicketCreation({
      ...ticketCreationData,
      attachmentsPath: attachmentsPath,
    });
  }

  onFetchTicketConfig(): Observable<any> {
    return this.managedServicesRepository.getTicketsCreateData();
  }

  onFetchTicketFormsConfig(ticketSubType: string): Observable<any> {
    return this.managedServicesRepository.getTicketsCreateConfig(ticketSubType);
  }

  /**
   * Open the ticket creation dialog
   * After the dialog is closed, navigate to the ticket details tab
   */
  openAddTicketDialog() {
    const dialog = this.dialog.open(TicketCreationFormsComponent, {
      panelClass: 'ticket-creation-form-dialog',
      data: {
        onUploadAttachment: this.overlayFileComponentUpload.bind(this),
        onDeleteAttachment: this.overlayFileComponentDelete.bind(this),
        onCreateTicket: this.onCreateTicket.bind(this),
        onFetchTicketConfig: this.onFetchTicketConfig.bind(this),
        onFetchTicketFormsConfig: this.onFetchTicketFormsConfig.bind(this),
        snackbarCreatedMessage:
          "Thank you for your submission. It's under review and will be assigned and reprioritized shortly",
        addTypesSubtypesInitialDescription: true,
      },
      autoFocus: false,
      disableClose: true,
      minWidth: '865px',
      maxWidth: '1100px',
      width: '100%',
    });
    dialog.afterClosed().subscribe((result) => {
      // Get response from dialog and navigate to the tickets details tab
      if (result?.ticketNumber) {
        if (result.failedToCreatedInHubDB) {
          this.snackBar.openFromComponent(SnackbarComponent, {
            data: {
              message: 'Your ticket was created successfully and you should be able to see it a few minutes',
              closeButton: true,
            },
            duration: 5000,
          });
          this.router.navigate(['/secured', 'managed-services', 'tickets']);
          return;
        }
        this.router.navigate(['/secured', 'managed-services', 'tickets', result.ticketNumber], {
          // Add the newlyCreated flag to indicate this is a newly created ticket
          // which will trigger the goBackWithRefresh method when navigating back
          queryParams: { newlyCreated: 'true' },
        });
      }
    });
  }

  private reviewAvailablePermissions() {
    this.userService.user$.subscribe(() => {
      this.canAddTicket = this.permissionsService.hasPermission('managed-services', 'Tickets', 'add');
    });
  }

  getTabMainText(tabName: string): string {
    const match = tabName.match(/^(.+?)(\s*\([^)]+\))?$/);
    return match ? match[1] : tabName;
  }

  hasSecondaryText(tabName: string): boolean {
    return /\([^)]+\)/.test(tabName);
  }

  getTabSecondaryText(tabName: string): string {
    const match = tabName.match(/\(([^)]+)\)/);
    return match ? `(${match[1]})` : '';
  }
}
